import { useMsal } from '@azure/msal-react'
import { Button, Group, Select, Stack, Table, Text, TextInput, Title } from '@mantine/core'
import { modals } from '@mantine/modals'
import { useEffect, useState } from 'react'
import { GemTag } from '../../../models/GemTag'
import { adminCreateTag, adminDeleteTag, adminUpdateTag, getAllTags } from '../../../data/communityGems.service'

const TAG_TYPE_OPTIONS = [
  { value: 'Community', label: 'Community (city/neighborhood)' },
  { value: 'Category', label: 'Category (type of place)' },
  { value: 'General', label: 'General' },
]

function TagsManager() {
  const { instance } = useMsal()
  const [tags, setTags] = useState<GemTag[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [newName, setNewName] = useState('')
  const [newType, setNewType] = useState<string>('Community')
  const [newOrder, setNewOrder] = useState<number>(0)

  useEffect(() => {
    getAllTags().then(setTags).finally(() => setLoading(false))
  }, [])

  const handleCreate = async () => {
    if (!newName.trim()) return
    const tag = await adminCreateTag(instance, { name: newName.trim(), tagType: newType as GemTag['tagType'], sortOrder: newOrder })
    setTags(prev => [...prev, tag].sort((a, b) => a.sortOrder - b.sortOrder))
    setNewName('')
    setNewOrder(0)
    setNewType('Community')
  }

  const handleUpdate = async (tag: GemTag) => {
    const updated = await adminUpdateTag(instance, tag.id, { name: tag.name, tagType: tag.tagType, sortOrder: tag.sortOrder })
    setTags(prev => prev.map(t => (t.id === updated.id ? updated : t)))
    setEditingId(null)
  }

  const handleDelete = (id: number) => {
    modals.openConfirmModal({
      title: 'Delete Tag',
      children: <Text size='sm'>Delete this tag? It will be removed from all Gemz.</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        await adminDeleteTag(instance, id)
        setTags(prev => prev.filter(t => t.id !== id))
      },
    })
  }

  if (loading) return <Text>Loading tags...</Text>

  return (
    <Stack mt='md'>
      <Title order={4}>Manage Tags</Title>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Sort Order</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tags.map(tag =>
            editingId === tag.id ? (
              <Table.Tr key={tag.id}>
                <Table.Td><TextInput value={tag.name} onChange={e => setTags(prev => prev.map(t => t.id === tag.id ? { ...t, name: e.target.value } : t))} /></Table.Td>
                <Table.Td>
                  <Select data={TAG_TYPE_OPTIONS} value={tag.tagType} onChange={val => setTags(prev => prev.map(t => t.id === tag.id ? { ...t, tagType: (val ?? 'Community') as GemTag['tagType'] } : t))} />
                </Table.Td>
                <Table.Td><TextInput type='number' value={tag.sortOrder} onChange={e => setTags(prev => prev.map(t => t.id === tag.id ? { ...t, sortOrder: Number(e.target.value) } : t))} /></Table.Td>
                <Table.Td>
                  <Group gap='xs'>
                    <Button size='xs' onClick={() => handleUpdate(tag)}>Save</Button>
                    <Button size='xs' variant='subtle' onClick={() => setEditingId(null)}>Cancel</Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ) : (
              <Table.Tr key={tag.id}>
                <Table.Td>{tag.name}</Table.Td>
                <Table.Td>{tag.tagType}</Table.Td>
                <Table.Td>{tag.sortOrder}</Table.Td>
                <Table.Td>
                  <Group gap='xs'>
                    <Button size='xs' variant='subtle' onClick={() => setEditingId(tag.id)}>Edit</Button>
                    <Button size='xs' variant='subtle' color='red' onClick={() => handleDelete(tag.id)}>Delete</Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            )
          )}
          <Table.Tr>
            <Table.Td><TextInput placeholder='New tag name' value={newName} onChange={e => setNewName(e.target.value)} /></Table.Td>
            <Table.Td><Select data={TAG_TYPE_OPTIONS} value={newType} onChange={val => setNewType(val ?? 'Community')} /></Table.Td>
            <Table.Td><TextInput type='number' value={newOrder} onChange={e => setNewOrder(Number(e.target.value))} /></Table.Td>
            <Table.Td><Button size='xs' onClick={handleCreate}>Add Tag</Button></Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </Stack>
  )
}

export default TagsManager
